/**
 * Der Verfasser im Chat – eine schwebende Pille (MEIN-TAG-v15.md 12.12).
 *
 * Eigenes Bauteil, nicht der `Verfasser` aus dem Kommentarfaden: Dort ist
 * das Schreiben ein Formular mit Anhang und Pfadangabe, hier ist es eine
 * Zeile. Die zweite Zeile mit Emoji-, @- und Büroklammer-Knopf und dem
 * Ablagepfad ist entfallen – der Pfad steht im ＋-Menü, und erwähnt wird
 * durch Tippen von „@".
 */

import { useEffect, useRef, useState } from 'react';

const EMOJIS = ['🙂', '😀', '😂', '👍', '🙏', '❤️', '🎉', '👌', '💪', '🤝', '⚠️', '✅', '❌', '📌', '📎', '🔧'];

export interface Anhangwahl {
  dateiname: string;
  url: string;
  ort: string;
}

export function ChatVerfasser({
  platzhalter,
  ablageHinweis,
  personen,
  antwortAuf,
  antwortFarbe,
  onAntwortWeg,
  onSenden,
  onTippt,
}: {
  platzhalter: string;
  /** „Team-Chat/2026-09" – steht nur im ＋-Menü. */
  ablageHinweis: string;
  personen: string[];
  antwortAuf: { autor: string; text: string } | null;
  antwortFarbe: string;
  onAntwortWeg: () => void;
  onSenden: (text: string, anhang: Anhangwahl | null) => void;
  onTippt: () => void;
}) {
  const [text, setText] = useState('');
  const [anhang, setAnhang] = useState<Anhangwahl | null>(null);
  const [plusOffen, setPlusOffen] = useState(false);
  const [emojiOffen, setEmojiOffen] = useState(false);
  const [url, setUrl] = useState('');
  /** Beim Tippen von „@" offen; `filter` sind die Buchstaben danach. */
  const [erwaehnen, setErwaehnen] = useState<{ ab: number; filter: string } | null>(null);
  const feld = useRef<HTMLTextAreaElement>(null);

  /* Wer auf „antworten" klickt, will tippen – nicht erst ins Feld klicken. */
  useEffect(() => {
    if (antwortAuf) feld.current?.focus();
  }, [antwortAuf]);

  /* Die Pille wächst mit dem Text, bis sie es nicht mehr soll. */
  useEffect(() => {
    const el = feld.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(120, el.scrollHeight)}px`;
  }, [text]);

  const passende = erwaehnen
    ? personen.filter((p) => p.toLowerCase().startsWith(erwaehnen.filter.toLowerCase())).slice(0, 6)
    : [];

  function schreiben(neu: string) {
    setText(neu);
    onTippt();
    /*
     * „@" öffnet die Liste, ein Leerzeichen schließt sie wieder. Gesucht
     * wird ab dem letzten „@" vor dem Cursor – so stört eine Mailadresse
     * weiter vorne im Text nicht.
     */
    const bis = feld.current?.selectionStart ?? neu.length;
    const davor = neu.slice(0, bis);
    const at = davor.lastIndexOf('@');
    if (at < 0 || /\s/.test(davor.slice(at + 1))) {
      setErwaehnen(null);
      return;
    }
    setErwaehnen({ ab: at, filter: davor.slice(at + 1) });
  }

  function erwaehnungSetzen(name: string) {
    if (!erwaehnen) return;
    const bis = feld.current?.selectionStart ?? text.length;
    const neu = `${text.slice(0, erwaehnen.ab)}@${name} ${text.slice(bis)}`;
    setText(neu);
    setErwaehnen(null);
    feld.current?.focus();
  }

  function senden() {
    const sauber = text.trim();
    if (!sauber) return;
    onSenden(sauber, anhang);
    setText('');
    setAnhang(null);
    setErwaehnen(null);
  }

  return (
    <div className="cv">
      {/* Die Mitgliederliste steht über der Pille, nicht darin. */}
      {erwaehnen && passende.length > 0 && (
        <div className="cv-erwaehnen">
          {passende.map((p, i) => (
            <button
              key={p}
              type="button"
              className={`cv-erwaehnen-zeile ${i === 0 ? 'erste' : ''}`}
              onClick={() => erwaehnungSetzen(p)}
            >
              {p}
            </button>
          ))}
        </div>
      )}

      {emojiOffen && (
        <div className="cv-emojis">
          {EMOJIS.map((e) => (
            <button
              key={e}
              type="button"
              className="cv-emoji"
              onClick={() => {
                setText((t) => t + e);
                setEmojiOffen(false);
                feld.current?.focus();
              }}
            >
              {e}
            </button>
          ))}
        </div>
      )}

      {plusOffen && (
        <div className="cv-plus-menue">
          <span className="cv-plus-titel">Datei anhängen</span>
          <span className="cv-plus-note">wird abgelegt unter {ablageHinweis || 'dem Vorgangsordner'}</span>
          <input
            className="cv-plus-feld"
            value={url}
            placeholder="Adresse der Datei im Vorgangsordner"
            onChange={(e) => setUrl(e.target.value)}
          />
          <button
            type="button"
            className="cv-plus-knopf"
            disabled={!/^https?:\/\//.test(url.trim())}
            onClick={() => {
              const roh = url.trim();
              const name = decodeURIComponent(roh.split('?')[0]!.split('/').pop() ?? 'Datei');
              setAnhang({ dateiname: name, url: roh, ort: ablageHinweis });
              setUrl('');
              setPlusOffen(false);
            }}
          >
            Anhängen
          </button>
        </div>
      )}

      <div className="cv-pille">
        {antwortAuf && (
          <div className="cv-antwort" style={{ borderLeftColor: antwortFarbe }}>
            <span className="cv-antwort-text">
              <span className="cv-antwort-wer">Antwort an {antwortAuf.autor}</span>
              <span className="cv-antwort-anfang">{antwortAuf.text}</span>
            </span>
            <button type="button" className="cv-x" title="Antwort verwerfen" onClick={onAntwortWeg}>
              ✕
            </button>
          </div>
        )}

        {anhang && (
          <div className="cv-anhang">
            <span>📎 {anhang.dateiname}</span>
            <button type="button" className="cv-x" title="Anhang entfernen" onClick={() => setAnhang(null)}>
              ✕
            </button>
          </div>
        )}

        <div className="cv-zeile">
          <button
            type="button"
            className="cv-rund"
            title="Anhängen oder Vorgang verlinken"
            onClick={() => {
              setPlusOffen((o) => !o);
              setEmojiOffen(false);
            }}
          >
            ＋
          </button>
          <button
            type="button"
            className="cv-rund"
            title="Emoji"
            onClick={() => {
              setEmojiOffen((o) => !o);
              setPlusOffen(false);
            }}
          >
            🙂
          </button>
          <textarea
            ref={feld}
            className="cv-feld"
            rows={1}
            value={text}
            placeholder={platzhalter}
            onChange={(e) => schreiben(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                setPlusOffen(false);
                setEmojiOffen(false);
                setErwaehnen(null);
                return;
              }
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                /* Bei offener Mitgliederliste übernimmt Enter den ersten Namen. */
                if (erwaehnen && passende[0]) erwaehnungSetzen(passende[0]);
                else senden();
              }
            }}
          />
          <button
            type="button"
            className="cv-senden"
            title="Senden"
            aria-label="Senden"
            disabled={!text.trim()}
            onClick={senden}
          >
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M4 12h15M13 6l6 6-6 6" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}
